import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'
import * as router from '@attain-apps/fe/web/main/lib/router'
import * as ngGridstack from 'gridstack/dist/angular'

import * as state from '../../../../../../../state'

import * as componentStore from './component-store'
import * as dialogs from './dialogs'
import { optimizeLayout } from './optimize-layout'
import * as overlay from './overlay'
import * as segments from './segments'
import * as widgetFrame from './widget-frame'

/**
 * Hosts the Mosaic Page Builder and its view-scoped component stores.
 */
@ngCore.Component({
  encapsulation: ngCore.ViewEncapsulation.None,
  imports: [
    ngGridstack.GridstackComponent,
    segments.Component,
    dialogs.widgetSelector.Component,
    dialogs.configuration.Component,
    dialogs.segment.Component,
    ds.layout.page.Component,
    overlay.overflowActions.Component,
    ds.layout.page.child.header.Component,
    ds.overlay.confirmation.Component,
  ],
  providers: [componentStore.builder.Store, componentStore.configuration.Store],
  selector: 'atn-mosaic-dev-page-builder-page',
  styleUrls: ['./component.scss'],
  templateUrl: './component.html',
})
export class Component implements ngCore.OnDestroy {
  /** @see {@link state.mosaic.dev.pages.Collection} */
  private readonly _pagesEc = ngCore.inject(state.mosaic.dev.pages.Collection)

  /** @see {@link componentStore.builder.Store} */
  public readonly builderStore = ngCore.inject(componentStore.builder.Store)

  /** @see {@link state.me.Feature} */
  public readonly meFeature = ngCore.inject(state.me.Feature)

  /** @see {@link router.Router} */
  private readonly _router = ngCore.inject(router.Router)

  /** Exposed button style for confirm layout optimization button within confirmation dialog */
  public readonly confirmLayoutOptimizationButtonStyle =
    ds.color.Legacy.PredefinedStyle.Primary

  /** Exposed button style for confirm discard changes button within confirmation dialog */
  public readonly confirmDiscardButtonStyle =
    ds.color.Legacy.PredefinedStyle.Danger

  /** {@link ngGridstack.NgGridStackOptions}  */
  public gridOptions: ngGridstack.NgGridStackOptions = {
    marginBottom: 20,
    marginLeft: 10,
    marginRight: 10,
    marginTop: 0,
  }

  constructor() {
    ngGridstack.GridstackComponent.addComponentToSelectorType([
      widgetFrame.Component,
    ])

    // Defers grid initialization to avoid ExpressionChangedAfterItHasBeenCheckedError.
    setTimeout(() => {
      this.builderStore.initializeGrid()
    })
  }

  /**
   * TODO: Remove this after implementing the logic and eliminating the other
   * dialog in Mosaic for page creation.
   */
  ngOnDestroy() {
    this._pagesEc.plugins.editingPlugin.feature.actions.discard
      .from('AtnMosaicDevPageBuilderPage.ngOnDestroy()')
      .dispatch({ current: true })
  }

  /**
   * Optimizing segment layout in case of creating new segment or switching to
   * another segment
   */
  public confirmClickHandler(): void {
    const grid = this.builderStore.gridSg()
    if (!grid) {
      return
    }

    optimizeLayout(grid, this.builderStore.widgetFrameLookupSg())
    // Save changes to current segment layout
    this.builderStore.saveCurrentSegmentLayout()

    const { isNew, segment } =
      this.builderStore.switchingSegmentConfigurationSg() ?? {}

    if (isNew) {
      this.builderStore.createSegment()
    } else {
      this.builderStore.setCurrentSegment(segment)
    }

    this.builderStore.patchState({
      switchingSegmentConfiguration: undefined,
    })
  }

  /** Closing the dialog and cancelling segment action*/
  public cancelSegmentOptimizeClickHandler(): void {
    this.builderStore.patchState({
      switchingSegmentConfiguration: undefined,
    })
  }

  /** Discarding changes and navigating to Mosaic Pages */
  public discardChangesBtnClickHandler(): void {
    const source = 'PageBuilder.Cancel.Clicked'
    this._pagesEc.plugins.editingPlugin.feature.actions.discard
      .from(source)
      .dispatch({
        current: true,
      })
    const url = this.meFeature.contexts.selectors
      .getUrlForLink({
        goto: {
          navigationId: ents.extra.prebuilt.AppPageAlias.Pages,
        },
      })
      .asSignal()

    if (url) {
      this._router.actions.navigate.from(source).dispatch({ url })
    }

    this.builderStore.patchState({
      markedForDiscard: false,
    })
  }

  /** Closing the dialog and cancelling discard action*/
  public cancelDiscardBtnClickHandler() {
    this.builderStore.patchState({
      markedForDiscard: false,
    })
  }
}
